import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IFifthWheelConnection, defaultValue } from 'app/shared/model/fifth-wheel-connection.model';

const initialState: EntityState<IFifthWheelConnection> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

interface IFifthWheelConnectionParams extends IQueryParams {
  truckImplementSearch?: string;
  dateSearch?: string;
  companyId?: number;
}

const apiUrl = 'api/fifth-wheel-connections';

// Actions

export const getEntities = createAsyncThunk(
  'fifthWheelConnection/fetch_entity_list',
  async ({ dateSearch, truckImplementSearch, companyId, page, size, sort }: IFifthWheelConnectionParams) => {
    const onDate = dateSearch ? `&date=${dateSearch}` : '';
    const onTruck = truckImplementSearch ? `&truckImplementSearch=${truckImplementSearch}` : '';
    const onCompanyId = companyId ? `&companyId=${companyId}` : '';
    const requestUrl = `${apiUrl}${
      sort ? `?page=${page}&size=${size}&sort=${sort}${onCompanyId}${onTruck}${onDate}&` : '?'
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<IFifthWheelConnection[]>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  'fifthWheelConnection/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IFifthWheelConnection>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'fifthWheelConnection/create_entity',
  async (entity: IFifthWheelConnection, thunkAPI) => {
    const result = await axios.post<IFifthWheelConnection>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'fifthWheelConnection/update_entity',
  async (entity: IFifthWheelConnection, thunkAPI) => {
    const result = await axios.put<IFifthWheelConnection>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'fifthWheelConnection/partial_update_entity',
  async (entity: IFifthWheelConnection, thunkAPI) => {
    const result = await axios.patch<IFifthWheelConnection>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'fifthWheelConnection/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IFifthWheelConnection>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const FifthWheelConnectionSlice = createEntitySlice({
  name: 'fifthWheelConnection',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = FifthWheelConnectionSlice.actions;

// Reducer
export default FifthWheelConnectionSlice.reducer;
